import React, { useRef, useEffect, useMemo, useCallback } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ResponsiveTree, useNodeMouseEventHandlers } from "@nivo/tree"

const NODE_H        = 68
const NODE_GAP      = 8
const LEVEL_SPACING = 160
const MARGIN_V      = 56

const LINE_H    = 16
const TITLE_H   = 12
const TITLE_GAP = 4

// Node width scales with available space per leaf: wider when few nodes, narrower when many.
// Clamped to [90, 130] so text is always readable.
const TARGET_INNER_W = 1200
const nodeWidthForUnits = (units) =>
  Math.max(90, Math.min(130, Math.floor(TARGET_INNER_W / units) - NODE_GAP))

// maxChars and font size scale with node width
const maxCharsForWidth  = (nw) => Math.floor((nw - 8) / 7)
const nameFontForWidth  = (nw) => Math.max(11, Math.min(14, Math.round(nw * 0.105)))
const titleFontForWidth = (nw) => Math.max(10, Math.min(12, Math.round(nw * 0.09)))

// --- data helpers -----------------------------------------------------------

const buildTree = (nodes) => {
  const byId = {}
  nodes.forEach(n => { byId[n.id] = { ...n, children: [] } })
  nodes.forEach(n => { if (n.parent_id && byId[n.parent_id]) byId[n.parent_id].children.push(byId[n.id]) })

  const roots = nodes.filter(n => !n.parent_id).map(n => byId[n.id])
  if (roots.length === 0) return null
  if (roots.length === 1) return roots[0]
  return { id: "__root__", name: "", bg_color: "transparent", text_color: "transparent", children: roots }
}

const getMaxDepth = (node, depth = 0) => {
  if (!node.children || node.children.length === 0) return depth
  return Math.max(...node.children.map(c => getMaxDepth(c, depth + 1)))
}

// Simulate d3 tree's separation function: +1 for same-parent siblings, +2 for different parents.
// Total layout units = span + 2 (half-unit padding on each edge).
// min leaf spacing = innerWidth / totalUnits
const computeTotalUnits = (root) => {
  const leavesParents = []
  const walk = (node, parentId) => {
    if (node.id === "__root__") { node.children?.forEach(c => walk(c, node.id)); return }
    if (!node.children || node.children.length === 0) {
      leavesParents.push(parentId)
    } else {
      node.children.forEach(c => walk(c, node.id))
    }
  }
  walk(root, null)

  if (leavesParents.length <= 1) return 2
  let span = 0
  for (let i = 1; i < leavesParents.length; i++) {
    span += leavesParents[i] === leavesParents[i - 1] ? 1 : 2
  }
  return span + 2
}

const wrapName = (name, maxChars) => {
  if (!name || name.length <= maxChars) return [name || ""]
  const spaceIdx = name.lastIndexOf(" ", maxChars)
  if (spaceIdx > 0) return [name.slice(0, spaceIdx), name.slice(spaceIdx + 1)]
  const hyphenIdx = name.lastIndexOf("-", maxChars)
  if (hyphenIdx > 0) return [name.slice(0, hyphenIdx + 1), name.slice(hyphenIdx + 1)]
  return [name.slice(0, maxChars), name.slice(maxChars)]
}

// --- node component ---------------------------------------------------------

const OrgNode = ({
  node, nodeWidth, maxChars, nameFontSize, titleFontSize, userPath,
  isInteractive, onMouseEnter, onMouseMove, onMouseLeave, onClick, setCurrentNode, tooltip,
}) => {
  const eventHandlers = useNodeMouseEventHandlers(node, {
    isInteractive: true, onMouseEnter, onMouseMove, onMouseLeave, onClick, setCurrentNode, tooltip,
  })

  if (node.id === "__root__") return null

  const bg    = node.data.bg_color   || "#4a90d9"
  const fg    = node.data.text_color || "#ffffff"
  const lines = wrapName(node.data.name, maxChars)
  const title = node.data.title || ""

  const nameBlockH = lines.length * LINE_H
  const totalH     = nameBlockH + (title ? TITLE_GAP + TITLE_H : 0)
  const startY     = -(totalH / 2)

  const handleClick = userPath && node.data.user_id
    ? () => { window.location.href = userPath.replace(":id", node.data.user_id) }
    : undefined

  return (
    <g
      transform={`translate(${node.x},${node.y})`}
      style={{ opacity: node.isActive === false ? 0.2 : 1, transition: "opacity 0.2s ease" }}
    >
      <rect
        x={-nodeWidth / 2}
        y={-NODE_H / 2}
        width={nodeWidth}
        height={NODE_H}
        rx={6}
        fill={bg}
        {...eventHandlers}
        style={handleClick ? { cursor: "pointer" } : undefined}
        onClick={handleClick}
      />
      {lines.map((line, i) => (
        <text
          key={i}
          x={0}
          y={startY + i * LINE_H + LINE_H / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={fg}
          style={{ fontSize: nameFontSize, fontWeight: 600, pointerEvents: "none", userSelect: "none" }}
        >
          {line}
        </text>
      ))}
      {title && (
        <text
          x={0}
          y={startY + nameBlockH + TITLE_GAP + TITLE_H / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={fg}
          style={{ fontSize: titleFontSize, opacity: 0.85, pointerEvents: "none", userSelect: "none" }}
        >
          {title}
        </text>
      )}
    </g>
  )
}

// --- chart panel ------------------------------------------------------------

const ChartPanel = ({ chartNodes, showHint, userPath }) => {
  const hasRelations = chartNodes && chartNodes.some(n => n.parent_id)
  const outerRef     = useRef(null)

  const { treeData, minWidth, chartHeight, nodeWidth, maxChars, nameFontSize, titleFontSize, marginH } = useMemo(() => {
    const defaults = { treeData: null, minWidth: 600, chartHeight: 400, nodeWidth: 110, maxChars: 14, nameFontSize: 12, titleFontSize: 10, marginH: 67 }
    if (!hasRelations) return defaults
    const tree = buildTree(chartNodes)
    if (!tree) return defaults

    const units = computeTotalUnits(tree)
    const depth = getMaxDepth(tree)
    const nw    = nodeWidthForUnits(units)
    const mc    = maxCharsForWidth(nw)
    const mh    = Math.round(nw / 2 + 12)

    return {
      treeData:      tree,
      nodeWidth:     nw,
      maxChars:      mc,
      nameFontSize:  nameFontForWidth(nw),
      titleFontSize: titleFontForWidth(nw),
      marginH:       mh,
      minWidth:      units * (nw + NODE_GAP) + 2 * mh,
      chartHeight:   Math.max(400, depth * LEVEL_SPACING + NODE_H + 2 * MARGIN_V),
    }
  }, [chartNodes, hasRelations])

  // Recreate nodeComponent only when layout params change
  const nodeComponent = useCallback(
    (props) => <OrgNode {...props} nodeWidth={nodeWidth} maxChars={maxChars} nameFontSize={nameFontSize} titleFontSize={titleFontSize} userPath={userPath} />,
    [nodeWidth, maxChars, nameFontSize, titleFontSize, userPath],
  )

  // When chart is wider than container, center the root
  useEffect(() => {
    const el = outerRef.current
    if (!el) return
    const overflow = el.scrollWidth - el.clientWidth
    if (overflow > 0) el.scrollLeft = overflow / 2
  }, [treeData])

  if (!hasRelations) {
    return (
      <div className="flex flex-col items-center justify-center text-slate-400" style={{ padding: "6rem 2rem" }}>
        <FontAwesomeIcon icon="sitemap" size="3x" />
        <p className="text-sm mt-4">{I18n.t("organigram.empty_chart")}</p>
        {showHint && (
          <p className="text-xs text-slate-400 mt-1">{I18n.t("organigram.empty_chart_hint")}</p>
        )}
      </div>
    )
  }

  return (
    <div ref={outerRef} style={{ overflowX: "auto", overflowY: "hidden" }}>
      <div style={{ minWidth, height: chartHeight }}>
        <ResponsiveTree
          data={treeData}
          layout="top-to-bottom"
          identity="id"
          mode="tree"
          nodeComponent={nodeComponent}
          enableLabel={false}
          linkCurve="bump"
          linkColor={{ from: "source.data.bg_color", modifiers: [["opacity", 0.4]] }}
          linkThickness={2}
          activeLinkThickness={6}
          inactiveLinkThickness={1}
          isInteractive={false}
          highlightAncestorNodes
          highlightAncestorLinks
          margin={{ top: MARGIN_V, right: marginH, bottom: MARGIN_V, left: marginH }}
          motionConfig="gentle"
          animate
        />
      </div>
    </div>
  )
}

export default ChartPanel
